import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import ArboxSignup from './ArboxSignup';
import BookingLog from './BookingLog';
import Classes from './Classes';
import { API_BASE } from './config';
import Profile from './Profile';
import { jwtDecode } from 'jwt-decode';
import SchedulesView from './Schedules';
import Admin from './Admin';

const appLogo = `${process.env.PUBLIC_URL}/gym.svg`;

function App() {
  const [jwt, setJwt] = useState(localStorage.getItem('jwt'));
  const [userEmail, setUserEmail] = useState(null);
  const [userName, setUserName] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState('classes'); // 'profile', 'classes', 'schedules', 'logs', 'admin'
  const [loginError, setLoginError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isPowerUser, setIsPowerUser] = useState(false);

  useEffect(() => {
    if (jwt) {
      // Fetch both profile and power user status
      Promise.all([
        axios.get(`${API_BASE}/profile`, {
          headers: { Authorization: `Bearer ${jwt}` }
        }),
        axios.get(`${API_BASE}/user/status`, {
          headers: { Authorization: `Bearer ${jwt}` }
        })
      ])
        .then(([profileRes, statusRes]) => {
          setAuthChecked(true);
          setLoginError(null);
          setIsPowerUser(statusRes.data.is_power_user || false);
        })
        .catch(() => {
          setJwt(null);
          localStorage.removeItem('jwt');
          setAuthChecked(true);
          setLoginError('Session expired or invalid. Please log in again.');
        });
    } else {
      setAuthChecked(true);
    }
  }, [jwt]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API_BASE}/login`, {
        token: credentialResponse.credential
      });
      if (response.data.token) {
        localStorage.setItem('jwt', response.data.token);

        // Store user applications data for multi-app support
        if (response.data.user_applications) {
          localStorage.setItem('user_applications', JSON.stringify(response.data.user_applications));

          // Create box-to-app mapping for quick lookup
          // We'll fetch this after login, but initialize empty for now
          localStorage.setItem('box_to_app_map', JSON.stringify({}));
        }

        setJwt(response.data.token);
        setAuthChecked(true);
        setLoginError(null);
        setIsLoggingIn(false);
      } else if (response.data.status === 'signup_required') {
        setShowSignup(true);
        setUserEmail(response.data.email || '');
        setGoogleToken(credentialResponse.credential); // Store Google token for secure signup
        // Decode Google ID token to get user's name
        try {
          const decoded = jwtDecode(credentialResponse.credential);
          setUserName(decoded.given_name || decoded.name || '');
        } catch (e) {
          setUserName('');
        }
        setLoginError(null);
        setIsLoggingIn(false);
      } else {
        setJwt(null);
        setAuthChecked(true);
        setLoginError(response.data.error || 'Login failed. Please try again.');
        setIsLoggingIn(false);
      }
    } catch (error) {
      setJwt(null);
      setAuthChecked(true);
      localStorage.removeItem('jwt');
      let errorMessage = 'Login failed. Please try again.';
      
      const status = error?.response?.status || error?.status;
      const serverError = error?.response?.data?.error || error?.message;

      if (status) {
        if (status === 401) {
          errorMessage = serverError || 'Unauthorized. Please check your credentials.';
        } else {
          errorMessage = serverError || `Server error (HTTP ${status}). Please try again.`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }

      setLoginError(errorMessage);
      setIsLoggingIn(false);

    }
  };

  const handleSignupSuccess = (token) => {
    setShowSignup(false);
    if (token) {
      localStorage.setItem('jwt', token);
      setJwt(token);
      setAuthChecked(false); // Will trigger useEffect to verify the token
    } else {
      setAuthChecked(true);
    }
    setLoginError(null);
  };

  const handleSignupError = (err) => {
    setJwt(null);
    setAuthChecked(true);
    setLoginError(err || 'Signup failed.');
  };

  const handleGoogleError = () => {
    setJwt(null);
    setAuthChecked(true);
    localStorage.removeItem('jwt');
    setLoginError('Google authentication failed. Please try again.');
    setIsLoggingIn(false);
  };

  const handleLogout = () => {
    setJwt(null);
    setUserEmail(null);
    setShowSignup(false);
    setAuthChecked(true);
    setCurrentPage('profile');
    localStorage.removeItem('jwt');
    // Clear multi-app data on logout
    localStorage.removeItem('user_applications');
    localStorage.removeItem('box_to_app_map');
    setLoginError(null);
    window.location.reload();
  };

  const BottomNavBar = () => (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
      <div className="flex justify-around items-center h-16 max-w-2xl mx-auto">
        <button
          onClick={() => setCurrentPage('profile')}
          className={`flex-1 flex flex-col items-center justify-center px-2 py-1 text-xs font-medium transition-colors ${
            currentPage === 'profile'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-blue-600'
          }`}
        >
          <span className="material-icons mb-1">person</span>
          Profile
        </button>
        <button
          onClick={() => setCurrentPage('classes')}
          className={`flex-1 flex flex-col items-center justify-center px-2 py-1 text-xs font-medium transition-colors ${
            currentPage === 'classes'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-blue-600'
          }`}
        >
          <span className="material-icons mb-1">event</span>
          Classes
        </button>
        <button
          onClick={() => setCurrentPage('schedules')}
          className={`flex-1 flex flex-col items-center justify-center px-2 py-1 text-xs font-medium transition-colors ${
            currentPage === 'schedules'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-blue-600'
          }`}
        >
          <span className="material-icons mb-1">schedule</span>
          Schedules
        </button>
        <button
          onClick={() => setCurrentPage('logs')}
          className={`flex-1 flex flex-col items-center justify-center px-2 py-1 text-xs font-medium transition-colors ${
            currentPage === 'logs'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-blue-600'
          }`}
        >
          <span className="material-icons mb-1">history</span>
          Activity
        </button>
        {isPowerUser && (
          <button
            onClick={() => setCurrentPage('admin')}
            className={`flex-1 flex flex-col items-center justify-center px-2 py-1 text-xs font-medium transition-colors ${
              currentPage === 'admin'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-blue-600'
            }`}
          >
            <span className="material-icons mb-1">admin_panel_settings</span>
            Admin
          </button>
        )}
      </div>
    </nav>
  );

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-slate-200">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-6"></div>
          <div className="text-xl font-semibold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  if (showSignup) {
    return <ArboxSignup onSuccess={handleSignupSuccess} onError={handleSignupError} email={userEmail} userName={userName} jwt={jwt} googleToken={googleToken} />;
  }

  if (jwt) {
    return (
      <div className="min-h-screen bg-gray-100 pb-16 flex flex-col">
        <div className="flex-1 flex flex-col">
          {currentPage === 'profile' ? (
            <Profile jwt={jwt} onLogout={handleLogout} />
          ) : currentPage === 'classes' ? (
            <Classes jwt={jwt} onLogout={handleLogout} />
          ) : currentPage === 'schedules' ? (
            <SchedulesView jwt={jwt} />
          ) : currentPage === 'logs' ? (
            <BookingLog jwt={jwt} isPowerUser={isPowerUser} />
          ) : currentPage === 'admin' ? (
            <Admin jwt={jwt} />
          ) : (
            <SchedulesView jwt={jwt} />
          )}
        </div>
        <BottomNavBar />
      </div>
    );
  }

  return (
    <div className="relative h-[100svh] overflow-hidden bg-[#f8fbff] flex items-center justify-center px-4 py-6 sm:py-10">
      <div className="login-ambient" aria-hidden="true">
        <span className="login-ambient__shape login-ambient__shape--cyan"></span>
        <span className="login-ambient__shape login-ambient__shape--rose"></span>
        <span className="login-ambient__shape login-ambient__shape--gold"></span>
      </div>

      <section className="relative z-10 w-full max-w-md overflow-hidden rounded-[2rem] border border-white/80 bg-white/85 p-8 shadow-[0_24px_80px_rgba(31,41,55,0.16)] backdrop-blur-xl sm:p-10">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#5fceff] via-[#ff4893] to-[#ffb578]" />
        <div className="flex flex-col items-center text-center">
          <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-white shadow-[0_18px_45px_rgba(79,70,163,0.18)] ring-1 ring-slate-100">
            <img src={appLogo} alt="Arboxer" className="h-16 w-16 object-contain" />
          </div>
          <h1 className="mb-8 text-5xl font-black tracking-[0.18em] text-slate-950 sm:text-6xl">
            ARBOXER
          </h1>
          {loginError && (
            <div className="mb-5 w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
              {loginError}
            </div>
          )}
          <div className="flex w-full justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              width="280"
            />
          </div>
        </div>
      </section>

      {isLoggingIn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm">
          <div className="flex flex-col items-center rounded-3xl bg-white/95 px-10 py-8 shadow-2xl">
            <div className="mb-5 h-16 w-16 animate-spin rounded-full border-4 border-[#5fceff] border-t-transparent"></div>
            <div className="text-lg font-semibold text-slate-800">Logging you in...</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
